import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { InvoiceListItemData } from "../InvoiceList/InvoiceList";
import { InvoiceStatus } from "../../../graphql/generated";
import invoiceStatusLabel from "../../../utils/labels/invoiceStatusLabel";
import { TableField } from "../../common/LynksTable/LynksTable";
import { useMemo } from "react";
import ShipmentLink from "../../common/ShipmentLink";
import { round, sum } from "lodash";
import LocaleProvider from "../../../providers/LocaleProvider";
import SmartLynksTable, {
  QueryFn,
} from "../../common/SmartLynksTable/SmartLynksTable";
import { testIds } from "./InvoiceBatchProcess.testIds";

type OnChangeProps = {
  status: InvoiceStatus;
};

export type InvoiceBatchProcessProps = {
  queryHook: QueryFn<InvoiceListItemData, "invoices">;
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  invoices: InvoiceListItemData[];
  setSelectedInvoices: (invoices: InvoiceListItemData[]) => void;
  onChange: (props: OnChangeProps) => void;
  statusBatchProcess: InvoiceStatus;
  isLoadingRequest: boolean;
  status: InvoiceStatus | null;
};

export const InvoiceBatchProcess = ({
  queryHook: query,
  open,
  onClose,
  onConfirm,
  invoices,
  onChange,
  statusBatchProcess,
  isLoadingRequest,
  status,
  setSelectedInvoices,
}: InvoiceBatchProcessProps) => {
  const { t } = useTranslation(["finance", "common"]);

  const InvoiceStatusSelect: Partial<Record<InvoiceStatus, string>> = {
    [InvoiceStatus.OnHold]: t("OnHold", "On Hold"),
    [InvoiceStatus.Draft]: t("InProcess", "In Process"),
    [InvoiceStatus.Sent]: t("ProcessAndSend", "Processs and Send"),
  };

  const fields: Array<TableField<InvoiceListItemData, InvoiceStatus>> = useMemo(
    () => [
      {
        label: "Status",
        type: "enum",
        value: (invoice) => invoice.status,
        valueLabel: invoiceStatusLabel,
        colors: {
          [InvoiceStatus.Draft]: "warning",
          [InvoiceStatus.Sent]: "primary",
          [InvoiceStatus.Paid]: "success",
          [InvoiceStatus.Cancelled]: "error",
          [InvoiceStatus.Closed]: "success",
          [InvoiceStatus.NotInvoiced]: "warning",
          [InvoiceStatus.OnHold]: "warning",
          [InvoiceStatus.Transferred]: "success",
          [InvoiceStatus.PendingCompletion]: "warning"
        },
        filterable: false,
      },
      {
        label: t("order"),
        type: "custom",
        value: (invoice) => (
          <Stack direction="row" spacing={2}>
            {(invoice.shipments || []).map((shipment) => (
              <ShipmentLink shipment={shipment} />
            ))}
          </Stack>
        ),
        sortBy: "shipment.shipmentNumber",
        filterable: false,
      },
      {
        label: t("invoiceNumber"),
        type: "string",
        value: "invoiceNumber",
        sortBy: "invoiceNumber",
        filterable: false,
      },
      {
        label: t("amount"),
        type: "string",
        value: (invoice) =>
          invoice.status !== InvoiceStatus.NotInvoiced
            ? `${LocaleProvider.getCurrencySymbol()} ${round(
                sum(invoice.charges.map((charge) => charge.unit * charge.rate)),
                2
              )}`
            : "",
        filterable: false,
      },
      {
        label: t("customer"),
        type: "custom",
        value: (invoice) =>
          invoice.customer ? (
            <Link data-testid={testIds.invoiceBatchProcessCustomer} href={`/customers/details/${invoice.customer._id}`}>
              {invoice.customer.name}
            </Link>
          ) : null,
        sortBy: "customer.name",
      },
      {
        label: t("shipper", "Shipper"),
        type: "custom",
        value: (invoice) => {
          const shippers = (invoice.shipments || []).flatMap((s) =>
            s.shipmentLocations.map((l) => l.shipper)
          );
          return (
            <Stack
              direction="row"
              alignItems="center"
              divider={
                <Typography
                  sx={{
                    mx: 0.5,
                  }}
                >
                  {" "}
                  /{" "}
                </Typography>
              }
            >
              {shippers.map((shipper) =>
                shipper ? (
                  <>
                    <Link data-testid={`${testIds.invoiceBatchProcessShipper}-${shipper._id}`} href={`/locations/details/${shipper._id}`}>
                      {shipper.name}
                    </Link>
                  </>
                ) : null
              )}
            </Stack>
          );
        },
        sortBy: "shipper.name",
      },
      {
        label: t("receiver", "Receiver"),
        type: "custom",
        value: (invoice) => {
          const receivers = (invoice.shipments || []).flatMap((s) =>
            s.shipmentLocations.map((l) => l.receiver)
          );
          return (
            <Stack
              direction="row"
              alignItems="center"
              divider={
                <Typography
                  sx={{
                    mx: 0.5,
                  }}
                >
                  {" "}
                  /{" "}
                </Typography>
              }
            >
              {receivers.map((receiver) =>
                receiver ? (
                  <>
                    <Link data-testid={`${testIds.invoiceBatchProcessReceiver}-${receiver._id}`} href={`/locations/details/${receiver._id}`}>
                      {receiver.name}
                    </Link>
                  </>
                ) : null
              )}
            </Stack>
          );
        },
        sortBy: "receiver.name",
      },
      {
        label: t("deliveryDate", "Delivery Date"),
        type: "datetime",
        value: (invoice) => invoice.shipments?.[0]?.trip?.lastDropoffTime,
        sortBy: "trip.lastDropoffTime",
      },
      {
        label: t("poNumber", "PO Number"),
        type: "string",
        value: (invoice) =>
          invoice.shipments
            ?.map((s) => s.purchaseOrderNumbers || [])
            .join(", "),
        filterable: false,
      },
      {
        label: t("referenceNumbers", "Reference Numbers"),
        type: "string",
        value: (invoice) =>
          invoice.shipments
            ?.flatMap((s) => s.referenceNumbers || [])
            .join(", "),
        filterable: false,
      },
      {
        label: t("bolNumbers", "BOL Numbers"),
        type: "string",
        value: (invoice) =>
          invoice.shipments
            ?.flatMap((s) => s.billOfLadingNumbers || [])
            .join(", "),
        filterable: false,
      },
      {
        label: t("ticketNumbers", "Ticket Numbers"),
        type: "string",
        value: (invoice) =>
          invoice.shipments?.flatMap((s) => s.ticketNumbers || []).join(", "),
        filterable: false,
      },
      {
        label: t("rateConNumbers", "Rate confirmation numbers"),
        type: "string",
        value: (invoice) =>
          invoice.shipments?.flatMap((s) => s.rateConNumbers || []).join(", "),
        filterable: false,
      },
      {
        label: t("tags", "Tags"),
        type: "string",
        value: (invoice) =>
          invoice.shipments?.flatMap((s) => s.tags || []).join(", "),
        sortBy: "shipment.tags",
      },
    ],
    [t]
  );

  return (
    <Dialog open={open} fullWidth maxWidth="lg">
      <DialogTitle>
        {t("invoiceBatchProcess", "Invoice Batch Process")}
      </DialogTitle>
      <DialogContent
        sx={{
          pt: 5,
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="end"
        >
          <Typography variant="body2">
            {t("setStatusTo", "Set status to")}
          </Typography>
          <Select data-testid={testIds.invoiceBatchProcessCondition}
            label="Condition"
            size="small"
            value={statusBatchProcess}
            onChange={(event) => {
              onChange({
                status: event.target.value as InvoiceStatus,
              });
            }}
          >
            {(Object.keys(InvoiceStatusSelect) as InvoiceStatus[]).map(
              (status) => (
                <MenuItem data-testid={`${testIds.invoiceBatchProcessStatus}-${status}`} key={status} value={status}>
                  <ListItemText primary={InvoiceStatusSelect[status]} />
                </MenuItem>
              )
            )}
          </Select>
        </Stack>
        <SmartLynksTable
          query={query}
          showGroups
          fields={fields}
          dataKey="invoices"
          id="invoice-list"
          additionalQueryVariables={{
            status,
          }}
          paginationMode="server"
          sortingMode="client"
          selectable
          onSelect={(selected) => {
            setSelectedInvoices(selected);
          }}
          bulkActionMenus={undefined}
        />
      </DialogContent>
      <DialogActions
        sx={{
          marginRight: 2,
        }}
      >
        <Button data-testid={testIds.invoiceBatchProcessCancel}
          onClick={() => {
            setSelectedInvoices([]);
            onClose();
          }}
        >
          {t("common:cancel")}
        </Button>
        <Button data-testid={testIds.invoiceBatchProcessConfirm}
          onClick={() => {
            onConfirm();
          }}
          variant="contained"
          id="save-closing-note"
          disabled={!invoices.length || isLoadingRequest}
        >
          {isLoadingRequest ? (
            <CircularProgress size={20} />
          ) : (
            t("common:confirm")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
